import { noop, isArray, some } from 'underscore';
import React, { useState, useEffect, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { getStoreQueriesWithLocalMock } from '@bingads-webui-campaign-react/merchant-shared';
import { Loading } from '@bingads-webui-campaign-react/loading';
import { Alert } from '@bingads-webui-react/alert';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getStyles } from './style';

const storeFormattedText = (providerId, getStoreResponse) => (getStoreResponse ? `ID: ${getStoreResponse.Id}, Name: ${getStoreResponse.Name}` : `ID: ${providerId}`);

const isStoreNotFoundFetchError = storeFetchError =>
  storeFetchError && isArray(storeFetchError.raw) && some(storeFetchError.raw, ({ ErrorCode }) => ErrorCode === 'StoreNotFoundErr');

const createStoreDetails = (providerId, getStoreResponse) => ({
  id: providerId,
  name: getStoreResponse && getStoreResponse.Name,
  storeUrl: getStoreResponse && getStoreResponse.DestinationUrl,
  text: storeFormattedText(providerId, getStoreResponse),
});

export function withStoreFromProviderId(WrappedComponent) {
  const HOC = (props) => {
    const {
      i18n,
      providerId,
      updateValidation,
    } = props;

    const [storeFetchState, setStoreFetchState] = useState({ fetchComplete: !providerId, storeDetails: null });
    const activity = useContext(ActivityContext);

    const storeQueries = useMemo(() => getStoreQueriesWithLocalMock(), []);

    useEffect(() => {
      if (providerId) {
        updateValidation(['store load in progress'], null, false /* shouldDirty */);
        storeQueries.getStore(providerId, true /* withCache */).then(
          (getStoreResponse) => {
            updateValidation([], null, false /* shouldDirty */);
            setStoreFetchState({ fetchComplete: true, storeDetails: createStoreDetails(providerId, getStoreResponse) });
          },
          (getStoreError) => {
            if (isStoreNotFoundFetchError(getStoreError)) {
              // deleted store and draft (placeholder) store scenarios will both return StoreNotFoundErr - we still return store details
              // with the information we have.
              if (activity) {
                activity.trace(`Store was not found for Store ID: ${providerId}`);
              }

              updateValidation([], null, false /* shouldDirty */);
              setStoreFetchState({ fetchComplete: true, storeDetails: createStoreDetails(providerId) });
            } else {
              if (activity) {
                activity.error(`Store load failed: ${getStoreError instanceof Error ? getStoreError : JSON.stringify(getStoreError)}`);
              }
              // TODO: This needs to be imporoved. Currently setting empty validation message to mar invalid but
              // to avoid showing message in withError HOC since this wrapper has it's own error message for load failure.
              updateValidation([''], null, false /* shouldDirty */);
              setStoreFetchState({ fetchComplete: true, getStoreError });
            }
          }
        );
      }
    }, [activity, providerId, storeQueries, updateValidation]);

    const classes = useDefaultStyles(getStyles);

    const loadingSpinner = useMemo(() => (
      <div className={classes.withStoreLoadingContainer}>
        <Loading text={i18n.getString(_TL_('Loading...'))} isVisible />
      </div>
    ), [classes.withStoreLoadingContainer, i18n]);

    const loadingError = useMemo(() => (
      <div className={classes.withStoreErrorContainer}>
        <Alert
          message={i18n.getString(_TL_('Cannot continue because store information failed to load. Please try again or contact support.'))}
          level="Error"
          icon={(<div className="alert-image" />)}
          dismissible={false}
        />
      </div>
    ), [classes.withStoreErrorContainer, i18n]);

    if (providerId && !storeFetchState.fetchComplete) {
      return loadingSpinner;
    }

    if (providerId && storeFetchState.fetchComplete && storeFetchState.getStoreError) {
      return loadingError;
    }

    return (
      <React.Fragment>
        <WrappedComponent {...props} storeDetails={storeFetchState.storeDetails} />
      </React.Fragment>
    );
  };

  HOC.propTypes = {
    i18n: PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    providerId: PropTypes.number,
    updateValidation: PropTypes.func,
  };

  HOC.defaultProps = {
    providerId: undefined,
    updateValidation: noop,
  };

  HOC.displayName = `withStoreFromProviderId(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(HOC, WrappedComponent);

  return HOC;
}
